import React, { useEffect, useRef } from 'react'

import Flyout from '../Flyout'
import { FlyoutMenuProps } from './FlyoutMenu.types'
import Link from 'next/link'
import { Text } from '@audio-network/componentlibrary'
import clsx from 'clsx'
import { shouldLinkToMusicApp } from '@/utils/shouldLinkToMusicApp'
import styles from './FlyoutMenu.module.css'
import { useSession } from '@/context/Session'

const FlyoutMenu: React.FC<FlyoutMenuProps> = ({
	className,
	id,
	isOpen,
	onClose,
	options,
	notification,
	...props
}) => {
	const flyoutActivated = useRef(false)
	const flyoutMenuRef = useRef(null)

	const { user } = useSession()

	useEffect(() => {
		const menuElement = flyoutMenuRef.current
		const mql =
			typeof matchMedia !== 'undefined' && matchMedia('(min-width: 48em)')

		if (isOpen) {
			if (!flyoutActivated.current) {
				flyoutActivated.current = true
			}

			if (mql?.matches && window?.innerHeight >= 0) {
				const { bottom, right } = menuElement.getBoundingClientRect()
				const { bottom: mainBottom } = document
					.querySelector('main')
					.getBoundingClientRect()
				const viewportWidth = window.innerWidth

				if (bottom >= mainBottom) {
					menuElement.style.bottom = '100%'
					menuElement.style.top = 'auto'
				}

				if (right > viewportWidth) {
					menuElement.style.right = '0'
					menuElement.style.left = 'auto'
				} else {
					menuElement.style.left = '0'
					menuElement.style.right = 'auto'
				}
			}
		} else if (mql?.matches && flyoutActivated?.current) {
			menuElement.addEventListener(
				'transitionend',
				() => {
					menuElement.style.bottom = null
					menuElement.style.top = null
					menuElement.style.left = null
					menuElement.style.right = null
				},
				{ once: true }
			)
		}
	}, [id, isOpen])

	const isMenuFocused = (): boolean => {
		const { activeElement } = document

		return activeElement?.id === id || !!activeElement?.closest(`#${id}`)
	}

	const handleClose = (): void => {
		if (isMenuFocused) {
			const button: HTMLElement = document.querySelector(
				`[aria-controls="${id}"]`
			)

			button.focus()
		}
		onClose()
	}

	const renderList = (): JSX.Element => {
		const handleClick = (
			event: MouseEvent,
			onClick: (event: MouseEvent) => void
		): void => {
			onClick && onClick(event)
			handleClose()
		}

		return (
			<ul className={styles.flyoutMenuList}>
				{notification && (
					<div
						data-testid="flyout-notification"
						className={styles.notification}
					>
						{notification.Icon}
						<Text variant="bodyS">{notification.message}</Text>
					</div>
				)}
				{options.map(
					({
						className,
						'data-action': dataAction,
						Icon,
						onClick,
						title,
						...option
					}) => {
						let OptionComponent: typeof Link | 'a' | 'button' | 'span' = 'span'

						const useLink =
							option.href && shouldLinkToMusicApp(option.href, !!user)

						if (useLink) {
							OptionComponent = Link
						}

						if (option.href && !useLink) {
							OptionComponent = 'a'
						}

						if (onClick && !option.href) {
							OptionComponent = 'button'
						}

						return (
							<li
								className={clsx(styles.flyoutMenuListItem, className)}
								data-action={dataAction}
								key={title}
							>
								<OptionComponent
									className={styles.flyoutMenuOption}
									// @ts-ignore
									onClick={(event: MouseEvent): void =>
										handleClick(event, onClick)
									}
									{...option}
								>
									{Icon && Icon}
									<span className={styles.title}>{title}</span>
								</OptionComponent>
							</li>
						)
					}
				)}
			</ul>
		)
	}

	return (
		<Flyout
			className={clsx(styles.flyoutMenu, className)}
			id={id}
			isOpen={isOpen}
			onClose={onClose}
			{...props}
			ref={flyoutMenuRef}
		>
			{renderList()}
			{/* Div allows sticky positioning of close button on mobile */}
			<div className={styles.flyoutMenuCloseButtonWrapper}>
				<button className={styles.flyoutMenuCloseButton} onClick={handleClose}>
					Close
				</button>
			</div>
		</Flyout>
	)
}

export default FlyoutMenu
